import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ChevronLeft } from "@/assets/icons/ds";
import { Button } from "@/components/Interface/Button/Button";
import { StepMenuItem } from "@/components/Interface/Stepper/StepMenu";
import { StepFlowLayout } from "@/components/Layout/BaseLayouts/StepFlowLayout/StepFlowLayout";
import { useSimIdFromQueryParam } from "@/hooks/useSimIdFromQueryParam";
import { portalRoutes } from "@/router/routes/portalRoutes";

const TOTAL_STEPS = 2;

interface RoamingBookingPageProps {
  dataTestid?: string;
  title: string;
  /** 1 = package selection, 2 = checkout. */
  step: 1 | 2;
  stepLabel: string;
  onBack: () => void;
  children: ReactNode;
}

/**
 * The 2-step roaming booking flow (package selection, checkout) in
 * StepFlowLayout, with an icon-only back button as header action. The step
 * menu mirrors SubscriptionChange: from the checkout the customer can jump
 * back to the package selection; the checkout itself is only reached by
 * continuing from step 1, since it needs the basket built there.
 */
export const RoamingBookingPage = ({
  dataTestid,
  title,
  step,
  stepLabel,
  onBack,
  children,
}: RoamingBookingPageProps) => {
  const { t } = useTranslation();
  const { t: tRoaming } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.roaming",
  });
  const [simId] = useSimIdFromQueryParam();

  const menuItems: StepMenuItem[] = [
    {
      label: tRoaming("addOption.stepLabel"),
      path: `${portalRoutes.manageDevice.roaming.booking}?simId=${simId}`,
      disabled: step === 1,
    },
    {
      label: tRoaming("checkout.stepLabel"),
      path: `${portalRoutes.manageDevice.roaming.checkout}?simId=${simId}`,
      disabled: true,
    },
  ];

  return (
    <StepFlowLayout
      dataTestid={dataTestid}
      title={title}
      step={step}
      totalSteps={TOTAL_STEPS}
      stepLabel={stepLabel}
      menuItems={menuItems}
      menuToggleLabel={t("portal:common.stepMenuLabel")}
      headerAction={
        <Button
          dataTestid="roaming-booking-back"
          variant="tertiary"
          ariaLabel={t("Common.label.back")}
          icon={<ChevronLeft />}
          onClick={onBack}
        />
      }
    >
      {children}
    </StepFlowLayout>
  );
};
